import {
  hlvVanillaMigrationIsActive,
  hlvVanillaPrereleaseUrlIsActive,
} from 'services/statsig/featureGates';
const IDENTITY_BASE_URL = process.env.REACT_APP_IDENTITY_BASE_URL;
const getVanillaHlvPathPrefix = (): '/forums' | '/new-hlv-forums' =>
  hlvVanillaPrereleaseUrlIsActive() ? '/new-hlv-forums' : '/forums';

const getVanillaHlvPath = (path: string): string =>
  `${getVanillaHlvPathPrefix()}${path}`;

/**
 * Note: if this file is changed, check if the changes also need to be made in community-ui:
 *  https://bitbucket.org/atlassian/community-ui/src/master/src/utils/url-generator.ts
 */

export type AuthQueryParamValues = {
  email?: string;
  redirectUrl?: string;
  prompt?: string;
  experience?: string;
};

const buildAuthQueryParams = (
  urlType: 'identity' | 'forums',
  {
    email,
    redirectUrl,
    prompt,
    experience = 'entry-auth',
  }: AuthQueryParamValues,
) => {
  const queryParams = [];
  if (email) {
    queryParams.push(`login_hint=${email}`);
  }
  if (experience) {
    queryParams.push(`application=community--${experience}`);
  }
  if (redirectUrl) {
    queryParams.push(
      urlType === 'identity'
        ? `continue=${redirectUrl}`
        : `referer=${redirectUrl}`,
    );
  }
  if (prompt) {
    queryParams.push(`prompt=${prompt}`);
  }
  return queryParams;
};

/**
 * Constructs a url to identity sign in page along with the eventual redirection page post successful sign in.
 * @param type - The type of authentication to perform.
 * @param queryParamValues - The query parameters to be passed to the authentication page.
 * @returns the url to identity login page
 */
export const getAuthUrl = (
  type: 'login' | 'signup' | 'logout',
  queryParamValues: AuthQueryParamValues,
): string => {
  const queryParams = buildAuthQueryParams('identity', queryParamValues);
  return `${IDENTITY_BASE_URL}/${type}?${queryParams.join('&')}`;
};

export const getForumsSignInUrl = (
  queryParamValues: AuthQueryParamValues,
): string => {
  if (hlvVanillaMigrationIsActive()) {
    const queryParams = [];
    // Only include Target parameter for OAuth2 SSO redirect
    if (queryParamValues.redirectUrl) {
      queryParams.push(
        `Target=${encodeURIComponent(queryParamValues.redirectUrl)}`,
      );
    }
    const queryString =
      queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return getVanillaHlvPath(`/entry/signin${queryString}`);
  }
  queryParamValues.prompt = 'none';
  const queryParams = buildAuthQueryParams('forums', queryParamValues);
  return `/forums/s/plugins/common/feature/oauth2sso_v2/sso_login_redirect?${queryParams.join(
    '&',
  )}`;
};

export const getForumsSignOutUrl = (redirectUrl?: string): string => {
  if (hlvVanillaMigrationIsActive()) {
    const queryParams = [];
    // Only include Target parameter for OAuth2 SSO redirect
    if (redirectUrl?.length) {
      queryParams.push(`Target=${encodeURIComponent(redirectUrl)}`);
    }
    const queryString =
      queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return getVanillaHlvPath(`/entry/signout${queryString}`);
  }
  return `forums/community/page.logoutpage?t:cp=authentication/contributions/unticketedauthenticationactions&referer=${encodeURIComponent(
    redirectUrl ?? '',
  )}&lia-action-token=vqbaOmbKji5FT8GhRqcJgN-gWIFDCKe-W-WZBph1FU0k_rYZvSl2UDf6uiPc8XUx&lia-action-token-id=logoff`;
};

/**
 * Forums profile URL for a user.
 * Vanilla: /forums/profile/discussions/:id
 * Legacy Khoros: /forums/user/viewprofilepage/user-id/:id?show=...
 */
export const getForumsProfileUrl = (
  forumsUserId: string,
  path: 'discussions' | 'notes',
  legacyShow: 'activity' | 'admin' = 'activity',
): string => {
  if (hlvVanillaMigrationIsActive()) {
    return `${window.location.origin}/forums/profile/${path}/${forumsUserId}`;
  }
  return `${window.location.origin}/forums/user/viewprofilepage/user-id/${forumsUserId}?show=${legacyShow}#details`;
};
